/**
 * Icon Library Node Component
 * Renders icon-based nodes from the icon library (AWS, Azure, GCP, etc.)
 */

import { memo, useState, useRef, useEffect } from 'react'
import { Handle, Position, NodeProps, NodeResizer, useReactFlow } from '@xyflow/react'
import type { NodeType } from '../../types/schema'
import GroupContainerNode from './GroupContainerNode'
import RotateHandle from './RotateHandle'

interface IconLibraryNodeData {
  label: string
  nodeType: NodeType
  subtype?: string
  diagramType: string
  icon?: string
  description?: string
  style?: any
  metadata?: Record<string, any>
  customData?: Record<string, any>
  minWidth?: number
  minHeight?: number
  maxWidth?: number
  maxHeight?: number
}

function IconLibraryNode({ id, data, selected }: NodeProps) {
  const nodeData = (data as unknown) as IconLibraryNodeData
  const shape = nodeData.style?.shape || getDefaultShape(nodeData.nodeType)
  const { setNodes } = useReactFlow()

  // Inline editing state
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(nodeData.label)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus and select text when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Update local edit value when node label changes externally
  useEffect(() => {
    setEditValue(nodeData.label)
  }, [nodeData.label])

  // Handle double-click to start editing
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditing(true)
  }

  // Save changes
  const saveEdit = () => {
    if (editValue.trim() !== nodeData.label) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, label: editValue.trim() } }
            : node
        )
      )
    }
    setIsEditing(false)
  }

  // Cancel editing
  const cancelEdit = () => {
    setEditValue(nodeData.label)
    setIsEditing(false)
  }

  // Handle keyboard events
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      saveEdit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEdit()
    }
  }

  // Handle click outside (blur)
  const handleBlur = () => {
    saveEdit()
  }

  // Get styles from data (priority) or fallback to defaults
  // Default: No fill, no border, no shadows (user can add if they want)
  const bgColor = (nodeData as any).bgColor !== undefined ? (nodeData as any).bgColor : (nodeData.style?.bgColor || 'transparent')
  const borderColor = (nodeData as any).borderColor !== undefined ? (nodeData as any).borderColor : (nodeData.style?.borderColor || 'transparent')
  const borderWidth = (nodeData as any).borderWidth !== undefined ? (nodeData as any).borderWidth : (nodeData.style?.borderWidth !== undefined ? nodeData.style.borderWidth : 0)
  const borderStyle = (nodeData as any).borderStyle || nodeData.style?.borderStyle || 'solid'
  const textColor = (nodeData as any).textColor || nodeData.style?.textColor || '#000000'
  const fontSize = (nodeData as any).fontSize !== undefined ? (nodeData as any).fontSize : (nodeData.style?.fontSize || 14)
  const fontWeight = (nodeData as any).fontWeight || nodeData.style?.fontWeight || '400'
  const fontFamily = (nodeData as any).fontFamily || nodeData.style?.fontFamily || 'Inter, sans-serif'
  const textAlign = (nodeData as any).textAlign || nodeData.style?.textAlign || 'center'
  const verticalAlign = (nodeData as any).verticalAlign || 'middle'
  const lineHeight = (nodeData as any).lineHeight || 1.5
  const textDecoration = (nodeData as any).textDecoration || 'none'
  const wordWrap = (nodeData as any).wordWrap !== false
  const textOverflow = (nodeData as any).textOverflow || 'clip'
  const textPadding = (nodeData as any).textPadding !== undefined ? (nodeData as any).textPadding : 12
  const letterSpacing = (nodeData as any).letterSpacing || 0
  const textOpacity = (nodeData as any).textOpacity !== undefined ? (nodeData as any).textOpacity : 1

  // Shadow settings
  const hasShadow = (nodeData as any).shadow === true
  const shadowColor = (nodeData as any).shadowColor || 'rgba(0, 0, 0, 0.2)'
  const shadowBlur = (nodeData as any).shadowBlur || 4

  // Draw.io-style grouping metadata (tree-based hierarchy)
  const groupData = (nodeData as any)?.group
  const groupDepth = groupData?.depth

  // Rotation (degrees). Applied to the node's VISUAL content only.
  // NOTE: edges and connection handles attach to the node's un-rotated bounding
  // box — rotating only the inner content keeps handles/resize predictable.
  const rotation = (nodeData as any).rotation || 0

  // Min/max resize limits
  const minWidth = nodeData.minWidth || 25
  const minHeight = nodeData.minHeight || 25
  const maxWidth = nodeData.maxWidth || 1000
  const maxHeight = nodeData.maxHeight || 1000

  // Calculate box shadow
  const getBoxShadow = () => {
    if (selected) {
      const selectionShadow = '0 0 0 3px #f59e0b'
      if (hasShadow) {
        return `${selectionShadow}, 0 ${shadowBlur}px ${shadowBlur * 2}px ${shadowColor}`
      }
      return selectionShadow
    }
    if (hasShadow) {
      return `0 ${shadowBlur}px ${shadowBlur * 2}px ${shadowColor}`
    }
    return 'none'
  }

  return (
    <div
      className="icon-library-node"
      data-group-depth={groupDepth}
      role="group"
      aria-label={`${nodeData.label || nodeData.nodeType || 'node'}${
        nodeData.nodeType ? ` (${nodeData.nodeType})` : ''
      }`}
      style={{
        width: '100%',
        height: '100%',
        padding: `${textPadding}px`,
        borderRadius: getShapeBorderRadius(shape),
        backgroundColor: bgColor,
        opacity: (nodeData as any).fillOpacity !== undefined ? (nodeData as any).fillOpacity : 1,
        border: `${borderWidth}px ${borderStyle} ${borderColor}`,
        color: textColor,
        fontFamily: fontFamily,
        minWidth: '120px',
        minHeight: '80px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center',
        justifyContent: verticalAlign === 'top' ? 'flex-start' : verticalAlign === 'bottom' ? 'flex-end' : 'center',
        boxShadow: getBoxShadow(),
        transition: 'all 0.2s ease',
        cursor: 'move',
      }}
    >
      {/* Resize handles */}
      <NodeResizer
        isVisible={selected}
        minWidth={minWidth}
        minHeight={minHeight}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        handleStyle={{
          width: 8,
          height: 8,
          borderRadius: 0,
          backgroundColor: '#3b82f6',
          border: '2px solid white',
        }}
        lineStyle={{
          borderWidth: 1,
          borderColor: '#3b82f6',
        }}
      />

      {/* Drag-rotate handle (single selection) */}
      {selected && <RotateHandle nodeId={id} />}

      {/* Rotatable visual content (icon + label + subtype + description).
          Handles/resizer stay un-rotated below so connections remain predictable. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'inherit',
          justifyContent: 'inherit',
          width: '100%',
          height: '100%',
          transform: rotation ? `rotate(${rotation}deg)` : undefined,
          transformOrigin: 'center',
        }}
      >
      {/* Icon or Shape */}
      {nodeData.icon && (
        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {nodeData.icon.startsWith('/icons/') || nodeData.icon.startsWith('/shapes/') ? (
            // Render as image if it's an icon or shape path
            <img
              src={nodeData.icon}
              alt={nodeData.label}
              style={{
                width: '48px',
                height: '48px',
                objectFit: 'contain',
              }}
            />
          ) : (
            // Render as emoji/text if it's not a path
            <span style={{ fontSize: '24px' }}>{nodeData.icon}</span>
          )}
        </div>
      )}

      {/* Label - Editable on double-click */}
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          style={{
            fontSize: fontSize,
            fontWeight: fontWeight,
            textAlign: textAlign as any,
            width: '100%',
            border: '2px solid #3b82f6',
            borderRadius: '4px',
            padding: '4px 8px',
            background: 'white',
            color: textColor,
            fontFamily: fontFamily,
            outline: 'none',
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <div
          onDoubleClick={handleDoubleClick}
          style={{
            fontSize: fontSize,
            fontWeight: fontWeight,
            textAlign: textAlign as any,
            lineHeight: lineHeight,
            textDecoration: textDecoration,
            letterSpacing: `${letterSpacing}px`,
            opacity: textOpacity,
            wordBreak: wordWrap ? 'break-word' : 'normal',
            whiteSpace: wordWrap ? 'normal' : 'nowrap',
            overflow: textOverflow === 'visible' ? 'visible' : textOverflow === 'clip' ? 'clip' : 'hidden',
            textOverflow: textOverflow === 'ellipsis' ? 'ellipsis' : 'clip',
            width: '100%',
            cursor: 'text',
          }}
        >
          {nodeData.label}
        </div>
      )}

      {/* Subtype badge */}
      {nodeData.subtype && (
        <div
          style={{
            fontSize: '10px',
            marginTop: '4px',
            padding: '2px 6px',
            borderRadius: '4px',
            backgroundColor: 'rgba(0,0,0,0.1)',
          }}
        >
          {nodeData.subtype}
        </div>
      )}

      {/* Description tooltip */}
      {nodeData.description && (
        <div
          style={{
            position: 'absolute',
            bottom: '-8px',
            right: '-8px',
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: '#3b82f6',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            cursor: 'help',
          }}
          title={nodeData.description}
        >
          ?
        </div>
      )}
      </div>
      {/* end rotatable visual content */}

      {/* 12 connection points (draw.io style) */}
      {/* Top handles */}
      <Handle type="source" position={Position.Top} id="top-left-source" style={{ ...handleStyle, left: '25%' }} />
      <Handle type="target" position={Position.Top} id="top-left-target" style={{ ...handleStyle, left: '25%' }} />
      <Handle type="source" position={Position.Top} id="top-center-source" style={{ ...handleStyle, left: '50%' }} />
      <Handle type="target" position={Position.Top} id="top-center-target" style={{ ...handleStyle, left: '50%' }} />
      <Handle type="source" position={Position.Top} id="top-right-source" style={{ ...handleStyle, left: '75%' }} />
      <Handle type="target" position={Position.Top} id="top-right-target" style={{ ...handleStyle, left: '75%' }} />

      {/* Right handles */}
      <Handle type="source" position={Position.Right} id="right-top-source" style={{ ...handleStyle, top: '25%' }} />
      <Handle type="target" position={Position.Right} id="right-top-target" style={{ ...handleStyle, top: '25%' }} />
      <Handle type="source" position={Position.Right} id="right-center-source" style={{ ...handleStyle, top: '50%' }} />
      <Handle type="target" position={Position.Right} id="right-center-target" style={{ ...handleStyle, top: '50%' }} />
      <Handle type="source" position={Position.Right} id="right-bottom-source" style={{ ...handleStyle, top: '75%' }} />
      <Handle type="target" position={Position.Right} id="right-bottom-target" style={{ ...handleStyle, top: '75%' }} />

      {/* Bottom handles */}
      <Handle type="source" position={Position.Bottom} id="bottom-left-source" style={{ ...handleStyle, left: '25%' }} />
      <Handle type="target" position={Position.Bottom} id="bottom-left-target" style={{ ...handleStyle, left: '25%' }} />
      <Handle type="source" position={Position.Bottom} id="bottom-center-source" style={{ ...handleStyle, left: '50%' }} />
      <Handle type="target" position={Position.Bottom} id="bottom-center-target" style={{ ...handleStyle, left: '50%' }} />
      <Handle type="source" position={Position.Bottom} id="bottom-right-source" style={{ ...handleStyle, left: '75%' }} />
      <Handle type="target" position={Position.Bottom} id="bottom-right-target" style={{ ...handleStyle, left: '75%' }} />

      {/* Left handles */}
      <Handle type="source" position={Position.Left} id="left-top-source" style={{ ...handleStyle, top: '25%' }} />
      <Handle type="target" position={Position.Left} id="left-top-target" style={{ ...handleStyle, top: '25%' }} />
      <Handle type="source" position={Position.Left} id="left-center-source" style={{ ...handleStyle, top: '50%' }} />
      <Handle type="target" position={Position.Left} id="left-center-target" style={{ ...handleStyle, top: '50%' }} />
      <Handle type="source" position={Position.Left} id="left-bottom-source" style={{ ...handleStyle, top: '75%' }} />
      <Handle type="target" position={Position.Left} id="left-bottom-target" style={{ ...handleStyle, top: '75%' }} />
    </div>
  )
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const handleStyle = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: '#2563eb',
  border: '2px solid white',
  opacity: 0, // Hidden by default, shown on hover via CSS
  transition: 'opacity 0.2s ease',
}

function getDefaultShape(nodeType: NodeType): string {
  const shapeMap: Record<string, string> = {
    // Architecture
    server: 'rounded',
    database: 'cylinder',
    cloud: 'cloud',
    storage: 'cylinder',
    queue: 'rounded',
    api: 'rounded',
    function: 'rounded',
    loadbalancer: 'rounded',
    container: 'rounded',

    // User Flow
    screen: 'rounded',
    actor: 'actor',
    action: 'rounded',
    annotation: 'note',

    // BPMN
    task: 'rounded',
    gateway: 'diamond',
    event: 'circle',
    subprocess: 'rounded',

    // ERD
    table: 'rectangle',
    view: 'rounded',
    procedure: 'rounded',

    // Flowchart
    process: 'rectangle',
    decision: 'diamond',
    input: 'parallelogram',
    output: 'parallelogram',
    start: 'ellipse',
    end: 'ellipse',
    document: 'document',

    // Mind Map / Org Chart
    node: 'ellipse',
    root: 'ellipse',
    branch: 'rounded',
    leaf: 'ellipse',
    person: 'rounded',
    team: 'rounded',
    department: 'rounded',
  }

  return shapeMap[nodeType] || 'rounded'
}

function getShapeBorderRadius(shape: string): string {
  switch (shape) {
    case 'rectangle':
      return '0'
    case 'rounded':
      return '8px'
    case 'ellipse':
    case 'circle':
      return '50%'
    case 'diamond':
      return '4px'
    default:
      return '8px'
  }
}

export default memo(IconLibraryNode)
export { GroupContainerNode as ContainerNode }
